<template>
  <div v-if="showCard" class="mb-6">
    <section aria-labelledby="notes-title">
      <Card>
        <div class="divide-y divide-gray-200">
          <div class="px-4 py-5 sm:px-6 flex justify-between">
            <h2 id="notes-title" class="text-lg font-medium text-gray-900">Interviews</h2>
            <div class="flex">
              <TButton
                class="ml-2"
                v-if="contact.interviews.length > notesIndex"
                label="Show All"
                color="white"
                @click="notesIndex = notesIndex + 999999"
              >
              </TButton>
              <TButton
                class="ml-2"
                v-else-if="contact.interviews.length > 5"
                label="Show Less"
                color="white"
                @click="notesIndex = 5"
              >
              </TButton>
            </div>
          </div>
          <div class="px-4 py-6 sm:px-6">
            <ul role="list" class="divide-y divide-gray-200">
              <li v-for="note of contact.interviews" :key="note.id" class="py-4">
                <div class="flex items-center justify-between">
                  <div class="flex space-x-3 items-center">
                    <div class="flex-shrink-0">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        class="w-6 h-6 text-gray-400"
                      >
                        <path
                          d="M12.75 12.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM7.5 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM8.25 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM9.75 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM10.5 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM12.75 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM14.25 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM15 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM16.5 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM15 12.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM16.5 13.5a.75.75 0 100-1.5.75.75 0 000 1.5z"
                        />
                        <path
                          fill-rule="evenodd"
                          d="M6.75 2.25A.75.75 0 017.5 3v1.5h9V3A.75.75 0 0118 3v1.5h.75a3 3 0 013 3v11.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V7.5a3 3 0 013-3H6V3a.75.75 0 01.75-.75zm13.5 9a1.5 1.5 0 00-1.5-1.5H5.25a1.5 1.5 0 00-1.5 1.5v7.5a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5v-7.5z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </div>
                    <div>
                      <div class="text-sm">
                        <div class="flex">
                          <div class="font-medium text-gray-900 mr-2">
                            {{ note.event_type === 'phone_screen' ? 'Phone Screen' : 'Interview' }}
                          </div>
                          <svg
                            v-if="note.status == 'active'"
                            class="flex-shrink-0 mr-1.5 h-5 w-5 text-green-400"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clip-rule="evenodd"
                            />
                          </svg>
                          <svg
                            v-else-if="note.status == 'canceled'"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            class="flex-shrink-0 mr-1.5 h-5 w-5 text-red-400"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z"
                              clip-rule="evenodd"
                            />
                          </svg>
                          <span class="capitalize font-medium text-gray-500 mr-1.5">
                            {{ note.status }}
                          </span>
                        </div>
                      </div>
                      <div class="mt-1 text-sm text-gray-700">
                        <p>
                          {{ note.name }} -
                          {{ formatDate(note.start_time, 'MMMM dd yyyy, h:mm a') }}
                          to {{ formatDate(note.end_time, 'h:mm a') }}
                        </p>
                      </div>
                      <div class="mt-2 space-x-2 text-sm">
                        <span class="font-medium text-gray-500"
                          >{{ new Date(note.end_time) > new Date() ? 'in' : '' }}
                          {{ fromNow(note.end_time) }}
                          {{ new Date(note.end_time) > new Date() ? '' : 'ago' }}</span
                        >
                      </div>
                    </div>
                  </div>
                  <div class="flex flex-col space-y-2" v-if="currentUser?.teamSlug">
                    <router-link
                      :to="`/contact_events/${note.id}/scorecard`"
                      v-if="showScorecardButton(note)"
                      class="rounded-md border border-gray-300 px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      >{{ viewScorecardText(note) }}</router-link
                    >
                    <a
                      :href="`/interview/${currentUser.teamSlug}?event_type=${note.event_type}&contact_id=${$route.params.id}`"
                      target="_blank"
                      style="color: #3b82f6"
                      class="mt-2 inline-block hover:text-blue-700 hover:underline text-sm font-medium text-center w-full"
                      role="menuitem"
                    >
                      Reschedule
                    </a>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </Card>
    </section>
  </div>
</template>

<script>
import Card from '../Card/index.vue';
import ContactEmailHistoryPreview from './ContactEmailHistoryPreview.vue';
import { sanitizeRichText } from '../../utilities/sanitization';
import { mapState } from 'vuex';
import DateUtility from '../../utilities/dates';

export default {
  props: ['contact'],
  components: {
    Card,
    ContactEmailHistoryPreview,
  },
  data() {
    return {
      contactNote: {
        content: '',
        followupAt: '',
      },
      notesIndex: 5,
    };
  },
  computed: {
    ...mapState({
      currentUser: state => state.general.currentUser,
    }),
    showCard() {
      if (!this.contact.interviews) return false;

      return this.contact.interviews.length > 0;
    },
  },
  async mounted() {
    await this.$store.dispatch('general/loadCurrentUser');
  },
  methods: {
    ...DateUtility,
    sanitize(html) {
      return sanitizeRichText(html);
    },
    showScorecardButton(note) {
      return (
        note.status != 'canceled' &&
        this.$store.state.general.currentUserAccountDetails?.featureFlags?.interview_scorecards
      );
    },
    viewScorecardText(note) {
      if (note.scorecard) {
        return 'View Scorecard';
      } else {
        return 'Script & Scorecard';
      }
    },
  },
};
</script>
